import {
  Activity,
  Calendar,
  Check,
  Eye,
  GitMerge,
  MapPin,
  Search,
  Trash2,
  UserCheck,
  X,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { ProviderMentionsList } from '@/components/treatment/encounters/ProviderMentionsList';
import { EnrichmentSummary } from '@/components/treatment/enrichment';
import { Button } from '@/components/ui/catalyst/button';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from '@/components/ui/catalyst/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Z_INDEX_MODAL_BASE,
  Z_INDEX_MODAL_NESTED,
} from '@/components/ui/zIndex';
import { useMatter } from '@/hooks/useMatter';
import {
  useApproveMedicalProviderMutation,
  useDeleteMedicalProviderMutation,
  useGetMedicalProvidersQuery,
  useMergeMedicalProvidersMutation,
  useRejectMedicalProviderMutation,
  useResetMedicalProviderStatusMutation,
  useUncullProviderMutation,
} from '@/services/api/treatmentService';
import {
  MedicalProvider,
  ProviderStatus,
} from '@/services/types/treatment-types';
import { getEnrichmentFromMentions } from '@/utils/enrichment-adapter';

const PendingProviderCard = ({
  provider,
  isBulkSelectMode,
  selectedPendingProviders,
  toggleProviderSelection,
  handleApprove,
  handleReject,
  setCurrentMergeProvider,
  setShowMergeModal,
}: {
  provider: MedicalProvider;
  isBulkSelectMode: boolean;
  selectedPendingProviders: Set<string>;
  handleApprove: (providerId: string) => void;
  handleReject: (providerId: string) => void;
  setCurrentMergeProvider: (providerId: string) => void;
  setShowMergeModal: (show: boolean) => void;
  toggleProviderSelection: (providerId: string) => void;
}) => {
  const isSelected = selectedPendingProviders.has(provider.id);
  const hasMentions = !!(provider.mentions && provider.mentions.length > 0);

  // Get enrichment data from mentions
  const enrichment = getEnrichmentFromMentions(provider.mentions);

  const { matterId } = useMatter();
  return (
    <div className="border border-zinc-200 rounded-lg bg-white overflow-hidden">
      {/* Left accent border for pending state */}
      <div className="border-l-4 border-violet-500 p-4">
        <div className="flex justify-between">
          {isBulkSelectMode && (
            <div className="mr-3 flex items-center">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-zinc-300"
                checked={isSelected}
                onChange={() => toggleProviderSelection(provider.id)}
              />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <h3 className="font-medium text-zinc-900 truncate">
              {provider.name || 'Unnamed Provider'}
            </h3>

            <div className="mt-1 space-y-0.5">
              {provider.address && (
                <div className="text-xs text-zinc-600">
                  Location: {provider.address}
                </div>
              )}
              {provider.treatment && (
                <div className="text-xs text-zinc-600">
                  Specialty: {provider.treatment}
                </div>
              )}
            </div>

            {/* Sources / Mentions - moved to top */}
            {hasMentions && provider.mentions && (
              <div className="mt-3 rounded-md border border-zinc-200 bg-white p-2">
                <ProviderMentionsList
                  mentions={provider.mentions}
                  expandedByDefault
                />
              </div>
            )}

            {/* Web Research / Enrichment section - simplified, no handles */}
            {enrichment && (
              <div className="mt-3 rounded-lg border border-violet-100 bg-gradient-to-r from-violet-50/50 to-transparent p-4">
                <EnrichmentSummary
                  enrichment={enrichment}
                  providerName={provider.name}
                  compact
                  hideHandles
                />
              </div>
            )}
          </div>
          {!isBulkSelectMode && (
            <div className="flex items-start gap-1 ml-3 flex-shrink-0">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Link
                    to={`/matters/${matterId}/treatment/${provider.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="h-7 w-7 border border-blue-200 bg-blue-50 text-blue-700 rounded hover:bg-blue-100 inline-flex items-center justify-center transition-colors"
                  >
                    <Eye className="h-3.5 w-3.5" />
                  </Link>
                </TooltipTrigger>
                <TooltipContent>View details</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    onClick={() => handleReject(provider.id)}
                    plain
                    className="!h-7 !w-7 !min-w-0 !p-0 border border-red-200 bg-red-50 text-red-700 rounded hover:bg-red-100 transition-colors inline-flex items-center justify-center"
                  >
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Reject</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    onClick={() => {
                      setCurrentMergeProvider(provider.id);
                      setShowMergeModal(true);
                    }}
                    plain
                    className="!h-7 !w-7 !min-w-0 !p-0 border border-amber-200 bg-amber-50 text-amber-700 rounded hover:bg-amber-100 transition-colors inline-flex items-center justify-center"
                  >
                    <GitMerge className="h-3.5 w-3.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Merge</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    onClick={() => handleApprove(provider.id)}
                    plain
                    className="!h-7 !w-7 !min-w-0 !p-0 border border-emerald-200 bg-emerald-50 text-emerald-700 rounded hover:bg-emerald-100 transition-colors inline-flex items-center justify-center"
                  >
                    <Check className="h-3.5 w-3.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Approve</TooltipContent>
              </Tooltip>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const ExistingProviderCard = ({ provider }: { provider: MedicalProvider }) => {
  return (
    <div className="border border-gray-200 rounded-lg p-4 bg-white hover:bg-gray-50 cursor-pointer">
      <div className="flex-1">
        <h3 className="font-medium text-gray-900">
          {provider.name || 'Unnamed Provider'}
        </h3>
        <div className="mt-2 space-y-1">
          {provider.address && (
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <MapPin className="h-3.5 w-3.5" />
              <span>{provider.address}</span>
            </div>
          )}
          {provider.treatment && (
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Activity className="h-3.5 w-3.5" />
              <span>{provider.treatment}</span>
            </div>
          )}
          {provider.created_at && (
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Calendar className="h-3.5 w-3.5" />
              <span>
                Added: {new Date(provider.created_at).toLocaleDateString()}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const StatusBadge = ({
  label,
  tone = 'red',
}: {
  label: string;
  tone?: 'red' | 'rose';
}) => {
  const palette =
    tone === 'rose' ? 'bg-rose-100 text-rose-700' : 'bg-red-100 text-red-700';
  return (
    <span
      className={`text-xs ${palette} px-2 py-0.5 rounded font-medium uppercase tracking-wide`}
    >
      {label}
    </span>
  );
};

const CulledProviderCard = ({
  provider,
  onUncull,
  onDelete,
  isDeleting,
}: {
  provider: MedicalProvider;
  onUncull: (providerId: string) => void;
  onDelete: (providerId: string) => void;
  isDeleting: boolean;
}) => {
  return (
    <div className="border border-red-200 rounded-lg p-4 bg-red-50 opacity-75">
      <div className="flex justify-between items-start">
        <div className="flex-1">
          <h3 className="font-medium text-gray-700 line-through">
            {provider.name || 'Unnamed Provider'}
          </h3>
          <div className="flex items-center gap-2 mt-2">
            <StatusBadge label="Culled" tone="red" />
            {provider.status === ProviderStatus.REJECTED && (
              <StatusBadge label="Rejected" tone="rose" />
            )}
          </div>
          <div className="mt-2 space-y-1">
            {provider.address && (
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <MapPin className="h-3.5 w-3.5" />
                <span>{provider.address}</span>
              </div>
            )}
            {provider.culled_reason && (
              <div className="text-sm text-red-600 mt-2">
                <strong>Reason:</strong> {provider.culled_reason}
              </div>
            )}
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <Button
            onClick={() => onUncull(provider.id)}
            plain
            className="p-2 bg-blue-600 rounded-md hover:bg-blue-700!"
            title="Restore Provider"
          >
            <UserCheck className="h-4 w-4 text-white" />
          </Button>
          <Button
            onClick={() => onDelete(provider.id)}
            plain
            disabled={isDeleting}
            className="p-2 bg-red-600 rounded-md hover:bg-red-700! disabled:opacity-60"
            title="Delete Provider"
          >
            <Trash2 className="h-4 w-4 text-white" />
          </Button>
        </div>
      </div>
    </div>
  );
};

const RejectedProviderCard = ({
  provider,
  onDelete,
  onRestore,
  isDeleting,
  isRestoring,
}: {
  provider: MedicalProvider;
  onDelete: (providerId: string) => void;
  onRestore: (providerId: string) => void;
  isDeleting: boolean;
  isRestoring: boolean;
}) => {
  return (
    <div className="border border-rose-200 rounded-lg p-4 bg-rose-50">
      <div className="flex justify-between items-start gap-3">
        <div className="flex-1">
          <h3 className="font-medium text-gray-900">
            {provider.name || 'Unnamed Provider'}
          </h3>
          <div className="mt-2 space-y-1">
            <StatusBadge label="Rejected" tone="rose" />
            {provider.address && (
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <MapPin className="h-3.5 w-3.5" />
                <span>{provider.address}</span>
              </div>
            )}
            <div className="text-sm text-rose-700 mt-2">
              This provider was explicitly rejected.
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <Button
            onClick={() => onRestore(provider.id)}
            plain
            disabled={isRestoring}
            className="p-2 bg-blue-600 rounded-md hover:bg-blue-700! disabled:opacity-60"
            title="Move back to pending"
          >
            <UserCheck className="h-4 w-4 text-white" />
          </Button>
          <Button
            onClick={() => onDelete(provider.id)}
            plain
            disabled={isDeleting}
            className="p-2 bg-red-600 rounded-md hover:bg-red-700! disabled:opacity-60"
            title="Delete Provider"
          >
            <Trash2 className="h-4 w-4 text-white" />
          </Button>
        </div>
      </div>
    </div>
  );
};

const MergeModalSearch = ({
  providers,
  onSelectProvider,
  selectedId,
}: {
  providers: MedicalProvider[];
  onSelectProvider: (id: string) => void;
  selectedId: string | null;
}) => {
  const [searchQuery, setSearchQuery] = useState('');

  const filteredProviders = useMemo(() => {
    if (!searchQuery) return providers;
    return providers.filter(
      (p) =>
        p.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.address?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.treatment?.toLowerCase().includes(searchQuery.toLowerCase()),
    );
  }, [providers, searchQuery]);

  return (
    <>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
        <input
          type="text"
          placeholder="Search for providers..."
          className="w-full bg-gray-50 rounded-md py-2 pl-10 pr-4 border border-gray-200"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      {/* Provider Options */}
      <div className="space-y-3 max-h-[400px] overflow-y-auto">
        {filteredProviders.map((provider) => (
          // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
          <div
            key={provider.id}
            className="border rounded-lg p-4 hover:bg-blue-50 cursor-pointer transition-colors"
            onClick={() => onSelectProvider(provider.id)}
          >
            <div className="flex items-start">
              <input
                type="radio"
                name="mergeTarget"
                className="mt-1 mr-3"
                checked={selectedId === provider.id}
                onChange={() => onSelectProvider(provider.id)}
              />
              <div className="flex-1">
                <h3 className="font-medium text-gray-900">{provider.name}</h3>
                <div className="mt-1 space-y-1">
                  {provider.address && (
                    <div className="text-sm text-gray-600">
                      Location: {provider.address}
                    </div>
                  )}
                  {provider.treatment && (
                    <div className="text-sm text-gray-600">
                      Specialty: {provider.treatment}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

interface ManageProvidersModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const ManageProvidersModal = ({
  isOpen,
  onClose,
}: ManageProvidersModalProps) => {
  const { matterId } = useMatter();

  // State
  const [pendingSearchQuery, setPendingSearchQuery] = useState('');
  const [existingSearchQuery, setExistingSearchQuery] = useState('');
  const [culledSearchQuery, setCulledSearchQuery] = useState('');
  const [rejectedSearchQuery, setRejectedSearchQuery] = useState('');
  const [selectedPendingProviders, setSelectedPendingProviders] = useState<
    Set<string>
  >(new Set());
  const [isBulkSelectMode, setIsBulkSelectMode] = useState(false);
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [mergeDestination, setMergeDestination] = useState<string | null>(null);
  const [currentMergeProvider, setCurrentMergeProvider] = useState<
    string | null
  >(null);

  // API queries and mutations
  const {
    data: providers = [],
    isLoading,
    refetch,
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  } = useGetMedicalProvidersQuery(matterId!, { skip: !matterId });
  const [approveProvider] = useApproveMedicalProviderMutation();
  const [rejectProvider] = useRejectMedicalProviderMutation();
  const [mergeProviders] = useMergeMedicalProvidersMutation();
  const [uncullProvider] = useUncullProviderMutation();
  const [deleteProvider] = useDeleteMedicalProviderMutation();
  const [resetProviderStatus] = useResetMedicalProviderStatusMutation();
  const [deletingProviderId, setDeletingProviderId] = useState<string | null>(
    null,
  );
  const [restoringProviderId, setRestoringProviderId] = useState<string | null>(
    null,
  );

  // Split providers by status
  const pendingProviders = useMemo(() => {
    return providers.filter(
      (p) =>
        !p.culled && (!p.status || p.status === ProviderStatus.NEEDS_APPROVAL),
    );
  }, [providers]);

  const existingProviders = useMemo(() => {
    return providers.filter(
      (p) => !p.culled && p.status === ProviderStatus.APPROVED,
    );
  }, [providers]);

  const culledProviders = useMemo(() => {
    return providers.filter((p) => p.culled);
  }, [providers]);

  const rejectedProviders = useMemo(() => {
    return providers.filter(
      (p) => !p.culled && p.status === ProviderStatus.REJECTED,
    );
  }, [providers]);

  // Filter providers based on search
  const filteredPendingProviders = useMemo(() => {
    if (!pendingSearchQuery) return pendingProviders;
    return pendingProviders.filter(
      (p) =>
        p.name?.toLowerCase().includes(pendingSearchQuery.toLowerCase()) ||
        p.address?.toLowerCase().includes(pendingSearchQuery.toLowerCase()) ||
        p.treatment?.toLowerCase().includes(pendingSearchQuery.toLowerCase()),
    );
  }, [pendingProviders, pendingSearchQuery]);

  const filteredExistingProviders = useMemo(() => {
    if (!existingSearchQuery) return existingProviders;
    return existingProviders.filter(
      (p) =>
        p.name?.toLowerCase().includes(existingSearchQuery.toLowerCase()) ||
        p.address?.toLowerCase().includes(existingSearchQuery.toLowerCase()) ||
        p.treatment?.toLowerCase().includes(existingSearchQuery.toLowerCase()),
    );
  }, [existingProviders, existingSearchQuery]);

  const filteredCulledProviders = useMemo(() => {
    if (!culledSearchQuery) return culledProviders;
    return culledProviders.filter(
      (p) =>
        p.name?.toLowerCase().includes(culledSearchQuery.toLowerCase()) ||
        p.address?.toLowerCase().includes(culledSearchQuery.toLowerCase()) ||
        p.culled_reason
          ?.toLowerCase()
          .includes(culledSearchQuery.toLowerCase()),
    );
  }, [culledProviders, culledSearchQuery]);

  const filteredRejectedProviders = useMemo(() => {
    if (!rejectedSearchQuery) return rejectedProviders;
    return rejectedProviders.filter(
      (p) =>
        p.name?.toLowerCase().includes(rejectedSearchQuery.toLowerCase()) ||
        p.address?.toLowerCase().includes(rejectedSearchQuery.toLowerCase()),
    );
  }, [rejectedProviders, rejectedSearchQuery]);

  // Only existing (non-culled) providers for merge destination selection
  const mergeDestinationProviders = useMemo(() => {
    return providers.filter(
      (p) => !p.culled && p.status === ProviderStatus.APPROVED,
    );
  }, [providers]);

  // Handlers
  const handleApprove = async (providerId: string) => {
    if (!matterId) return;
    try {
      await approveProvider({ matterId, providerId }).unwrap();
      toast.success('Provider approved successfully');
      refetch();
    } catch (error) {
      toast.error('Failed to approve provider');
    }
  };

  const handleReject = async (providerId: string) => {
    if (!matterId) return;
    try {
      await rejectProvider({ matterId, providerId }).unwrap();
      toast.success('Provider rejected');
      refetch();
    } catch (error) {
      toast.error('Failed to reject provider');
    }
  };

  const handleUncull = async (providerId: string) => {
    if (!matterId) return;
    try {
      await uncullProvider({ matterId, providerId }).unwrap();
      toast.success('Provider restored successfully');
      refetch();
    } catch (error) {
      toast.error('Failed to restore provider');
    }
  };

  const handleMerge = async () => {
    if (!matterId || !mergeDestination) return;

    // Determine source IDs based on whether we're in bulk mode or single merge
    let sourceIds: string[];
    if (selectedPendingProviders.size > 0) {
      // Bulk merge mode
      sourceIds = Array.from(selectedPendingProviders);
    } else if (currentMergeProvider) {
      // Single provider merge
      sourceIds = [currentMergeProvider];
    } else {
      return;
    }

    try {
      await mergeProviders({
        matterId,
        mergeProviderRequest: {
          dst_provider_id: mergeDestination,
          src_provider_ids: sourceIds,
        },
      }).unwrap();
      toast.success(`Successfully merged ${sourceIds.length} provider(s)`);

      // Reset state
      setSelectedPendingProviders(new Set());
      setMergeDestination(null);
      setShowMergeModal(false);
      setIsBulkSelectMode(false);
      setCurrentMergeProvider(null);
      refetch();
    } catch (error) {
      toast.error('Failed to merge providers');
    }
  };

  const toggleProviderSelection = (providerId: string) => {
    const newSelection = new Set(selectedPendingProviders);
    if (newSelection.has(providerId)) {
      newSelection.delete(providerId);
    } else {
      newSelection.add(providerId);
    }
    setSelectedPendingProviders(newSelection);
  };

  const selectAllFiltered = () => {
    const allIds = new Set(filteredPendingProviders.map((p) => p.id));
    setSelectedPendingProviders(allIds);
  };

  const clearSelection = () => {
    setSelectedPendingProviders(new Set());
    setIsBulkSelectMode(false);
  };

  const handleDelete = async (providerId: string) => {
    if (!matterId) {
      return;
    }
    // eslint-disable-next-line no-alert
    const confirmed = window.confirm(
      'Deleting a provider permanently removes it and associated tasks. Continue?',
    );
    if (!confirmed) {
      return;
    }
    try {
      setDeletingProviderId(providerId);
      await deleteProvider({ matterId, providerId }).unwrap();
      toast.success('Provider deleted successfully');
      await refetch();
    } catch (error) {
      toast.error('Failed to delete provider');
    } finally {
      setDeletingProviderId(null);
    }
  };

  const handleRestoreRejected = async (providerId: string) => {
    if (!matterId) {
      return;
    }
    try {
      setRestoringProviderId(providerId);
      await resetProviderStatus({ matterId, providerId }).unwrap();
      toast.success('Provider moved back to pending');
      await refetch();
    } catch (error) {
      toast.error('Failed to restore provider');
    } finally {
      setRestoringProviderId(null);
    }
  };

  if (!matterId) {
    return null;
  }

  return (
    <>
      <Dialog
        open={isOpen}
        onClose={onClose}
        size="6xl"
        zIndex={Z_INDEX_MODAL_BASE}
      >
        <DialogTitle>Manage Providers</DialogTitle>
        <DialogBody className="p-0">
          <div className="flex h-[600px] overflow-x-auto">
            {/* Left Pane - Pending Providers */}
            <div className="w-2/5 min-w-[320px] flex-shrink-0 border-r border-gray-200 p-4 flex flex-col">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div className="h-2 w-2 bg-amber-500 rounded-full" />
                  <h2 className="text-lg font-semibold">Pending Approvals</h2>
                </div>
                <span className="text-sm text-gray-500">
                  {pendingProviders.length}
                </span>
              </div>

              {/* Search and Controls */}
              <div className="mb-4 space-y-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                  <input
                    type="text"
                    placeholder="Filter pending providers..."
                    className="w-full bg-gray-50 rounded-md py-2 pl-10 pr-4 border border-gray-200"
                    value={pendingSearchQuery}
                    onChange={(e) => setPendingSearchQuery(e.target.value)}
                  />
                </div>

                {/* Bulk Selection Controls */}
                <div className="flex items-center justify-between">
                  <label
                    className="flex items-center gap-2 cursor-pointer"
                    htmlFor="bulk-select"
                  >
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-gray-300"
                      checked={isBulkSelectMode}
                      onChange={(e) => {
                        setIsBulkSelectMode(e.target.checked);
                        if (!e.target.checked) {
                          clearSelection();
                        }
                      }}
                    />
                    <span className="text-sm font-medium">Select multiple</span>
                  </label>

                  {isBulkSelectMode && (
                    <div className="flex items-center gap-3">
                      <label
                        className="flex items-center gap-2 cursor-pointer"
                        htmlFor="select-all-filtered"
                      >
                        <input
                          id="select-all-filtered"
                          type="checkbox"
                          className="h-4 w-4 rounded border-gray-300"
                          checked={
                            selectedPendingProviders.size ===
                              filteredPendingProviders.length &&
                            filteredPendingProviders.length > 0
                          }
                          onChange={(e) => {
                            if (e.target.checked) {
                              selectAllFiltered();
                            } else {
                              clearSelection();
                            }
                          }}
                        />
                        <span className="text-sm">Select all filtered</span>
                      </label>
                      {selectedPendingProviders.size > 0 && (
                        <>
                          <span className="text-sm text-gray-600">
                            {selectedPendingProviders.size} selected
                          </span>
                          <Button
                            onClick={() => setShowMergeModal(true)}
                            plain
                            className="px-3 py-1.5 bg-blue-600 text-white! rounded-md hover:bg-blue-700 flex items-center gap-1.5 text-sm"
                          >
                            <GitMerge className="h-3.5 w-3.5" />
                            Bulk Merge
                          </Button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Pending Providers List */}
              <div className="space-y-3 overflow-y-auto flex-1 min-h-0">
                {/* eslint-disable-next-line no-nested-ternary */}
                {isLoading ? (
                  <div className="text-center py-8 text-gray-500">
                    Loading providers...
                  </div>
                ) : filteredPendingProviders.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    {pendingSearchQuery
                      ? 'No matching providers found'
                      : 'No pending providers'}
                  </div>
                ) : (
                  filteredPendingProviders.map((provider) => (
                    <PendingProviderCard
                      key={provider.id}
                      provider={provider}
                      isBulkSelectMode={isBulkSelectMode}
                      selectedPendingProviders={selectedPendingProviders}
                      toggleProviderSelection={toggleProviderSelection}
                      handleApprove={handleApprove}
                      handleReject={handleReject}
                      setCurrentMergeProvider={setCurrentMergeProvider}
                      setShowMergeModal={setShowMergeModal}
                    />
                  ))
                )}
              </div>
            </div>

            {/* Middle Pane - Existing Providers */}
            <div className="w-[30%] min-w-[240px] flex-shrink-0 border-r border-gray-200 p-4 flex flex-col">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div className="h-2 w-2 bg-green-600 rounded-full" />
                  <h2 className="text-lg font-semibold">Existing Providers</h2>
                </div>
                <span className="text-sm text-gray-500">
                  {existingProviders.length}
                </span>
              </div>

              {/* Search */}
              <div className="mb-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                  <input
                    type="text"
                    placeholder="Filter existing providers..."
                    className="w-full bg-gray-50 rounded-md py-2 pl-10 pr-4 border border-gray-200"
                    value={existingSearchQuery}
                    onChange={(e) => setExistingSearchQuery(e.target.value)}
                  />
                </div>
              </div>

              {/* Existing Providers List */}
              <div className="space-y-3 overflow-y-auto flex-1 min-h-0">
                {filteredExistingProviders.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    {existingSearchQuery
                      ? 'No matching providers found'
                      : 'No existing providers'}
                  </div>
                ) : (
                  filteredExistingProviders.map((provider) => (
                    <ExistingProviderCard
                      key={provider.id}
                      provider={provider}
                    />
                  ))
                )}
              </div>
            </div>

            {/* Right Pane - Culled and Rejected Providers */}
            <div className="w-[30%] min-w-[240px] flex-shrink-0 p-4 space-y-8 overflow-y-auto">
              <section>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <div className="h-2 w-2 bg-red-500 rounded-full" />
                    <h2 className="text-lg font-semibold">Culled Providers</h2>
                  </div>
                  <span className="text-sm text-gray-500">
                    {culledProviders.length}
                  </span>
                </div>

                <div className="mb-4">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                    <input
                      type="text"
                      placeholder="Filter culled providers..."
                      className="w-full bg-gray-50 rounded-md py-2 pl-10 pr-4 border border-gray-200"
                      value={culledSearchQuery}
                      onChange={(e) => setCulledSearchQuery(e.target.value)}
                    />
                  </div>
                </div>

                <div className="space-y-3 overflow-y-auto max-h-[200px]">
                  {filteredCulledProviders.length === 0 ? (
                    <div className="text-center py-6 text-gray-500">
                      {culledSearchQuery
                        ? 'No matching providers found'
                        : 'No culled providers'}
                    </div>
                  ) : (
                    filteredCulledProviders.map((provider) => (
                      <CulledProviderCard
                        key={provider.id}
                        provider={provider}
                        onUncull={handleUncull}
                        onDelete={handleDelete}
                        isDeleting={deletingProviderId === provider.id}
                      />
                    ))
                  )}
                </div>
              </section>

              <section>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <div className="h-2 w-2 bg-rose-500 rounded-full" />
                    <h2 className="text-lg font-semibold">
                      Rejected Providers
                    </h2>
                  </div>
                  <span className="text-sm text-gray-500">
                    {rejectedProviders.length}
                  </span>
                </div>

                <div className="mb-4">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                    <input
                      type="text"
                      placeholder="Filter rejected providers..."
                      className="w-full bg-gray-50 rounded-md py-2 pl-10 pr-4 border border-gray-200"
                      value={rejectedSearchQuery}
                      onChange={(e) => setRejectedSearchQuery(e.target.value)}
                    />
                  </div>
                </div>

                <div className="space-y-3 overflow-y-auto max-h-[200px]">
                  {filteredRejectedProviders.length === 0 ? (
                    <div className="text-center py-6 text-gray-500">
                      {rejectedSearchQuery
                        ? 'No matching providers found'
                        : 'No rejected providers'}
                    </div>
                  ) : (
                    filteredRejectedProviders.map((provider) => (
                      <RejectedProviderCard
                        key={provider.id}
                        provider={provider}
                        onDelete={handleDelete}
                        onRestore={handleRestoreRejected}
                        isDeleting={deletingProviderId === provider.id}
                        isRestoring={restoringProviderId === provider.id}
                      />
                    ))
                  )}
                </div>
              </section>
            </div>
          </div>
        </DialogBody>
        <DialogActions>
          <Button onClick={onClose} color="white">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Merge Modal */}
      {showMergeModal && (
        <Dialog
          open={showMergeModal}
          onClose={() => {
            setShowMergeModal(false);
            setCurrentMergeProvider(null);
            setMergeDestination(null);
          }}
          size="2xl"
          zIndex={Z_INDEX_MODAL_NESTED}
          nested
        >
          <DialogTitle>Merge with Provider</DialogTitle>
          <DialogBody>
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Select an existing provider to merge with. The selected provider
                will be kept, and all data from the{' '}
                {selectedPendingProviders.size > 0
                  ? `${selectedPendingProviders.size} selected`
                  : 'source'}{' '}
                provider(s) will be transferred.
              </p>

              {/* Search */}
              <MergeModalSearch
                providers={mergeDestinationProviders}
                onSelectProvider={setMergeDestination}
                selectedId={mergeDestination}
              />
            </div>
          </DialogBody>
          <DialogActions>
            <Button
              onClick={() => {
                setShowMergeModal(false);
                setCurrentMergeProvider(null);
                setMergeDestination(null);
              }}
              color="white"
            >
              Cancel
            </Button>
            <Button
              onClick={handleMerge}
              color="blue"
              disabled={!mergeDestination}
            >
              <GitMerge className="h-4 w-4 mr-2" />
              Complete Merge
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};
